import React from 'react'
import {
  Box,
  Group,
  Stack,
  Paper,
  Alert
} from '@mantine/core'
import {
  FiHeart,
  FiShare2,
  FiTrash2,
  FiMessageSquare,
  FiSend,
  FiCornerDownRight,
  FiEdit2
} from 'react-icons/fi'
import { OneLevelCommentList, CommentInputRow } from '@gunslop/ui'
import type { PlatformCommentNode } from '@gunslop/ui'
import AppModal from '../../app/common/ui/AppModal'
import AppButton from '../../app/common/ui/AppButton'
import AppIconButton from '../../app/common/ui/AppIconButton'
import AppText from '../../app/common/ui/AppText'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import { store } from '../../app/stores/store'
import { formatSlopDate } from './slopFeedConstants'
import type { SlopLibraryFeed } from './hooks/useSlopLibraryFeed'
import {
  SLOP_FEED_MOBILE_MQ,
  FEED_ACTION_BAR_SX,
  slopDetailNativeVideoSx,
  slopDetailVideoBleedSx,
  slopDetailEmbedSx,
  slopPostDetailModalStyles,
  slopThumbActionIconSx
} from './slopFeedLayout'

type Props = Pick<
  SlopLibraryFeed,
  | 'selectedPost'
  | 'setSelectedPost'
  | 'isLoggedIn'
  | 'isAdmin'
  | 'history'
  | 'comments'
  | 'commentsLoading'
  | 'newComment'
  | 'setNewComment'
  | 'commentSubmitting'
  | 'replyingToId'
  | 'setReplyingToId'
  | 'replyText'
  | 'setReplyText'
  | 'shareCopied'
  | 'requireAuth'
  | 'handleToggleLike'
  | 'handleShare'
  | 'handleAddComment'
  | 'handleAddReply'
  | 'handleDeleteComment'
  | 'setDeleteConfirmId'
>

export default function SlopPostDetailModal (props: Props) {
  const {
    selectedPost,
    setSelectedPost,
    isLoggedIn,
    isAdmin,
    history,
    comments,
    commentsLoading,
    newComment,
    setNewComment,
    commentSubmitting,
    replyingToId,
    setReplyingToId,
    replyText,
    setReplyText,
    shareCopied,
    requireAuth,
    handleToggleLike,
    handleShare,
    handleAddComment,
    handleAddReply,
    handleDeleteComment,
    setDeleteConfirmId
  } = props

  const currentUser = store.userStore.user

  const scrollToComments = () => {
    document.getElementById('slop-modal-comments')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const renderCommentActions = (comment: PlatformCommentNode, depth: number) => {
    if (!selectedPost) return null
    const postId = selectedPost.id
    const canDelete =
      comment.authorUsername === currentUser?.userName || isAdmin

    return (
      <Group spacing={4} mt={4}>
        {depth === 0 && isLoggedIn && (
          <AppButton
            variant="subtle"
            size="xs"
            compact
            leftIcon={<FiCornerDownRight size={12} />}
            sx={{ color: 'var(--gs-muted)', fontSize: 11 }}
            onClick={() => setReplyingToId(replyingToId === comment.id ? null : comment.id)}
          >
            Reply
          </AppButton>
        )}
        {canDelete && (
          <AppButton
            variant="subtle"
            size="xs"
            compact
            leftIcon={<FiTrash2 size={12} />}
            sx={{ color: 'var(--gs-muted)' }}
            onClick={() => {
              const parentId = depth > 0 ? (comment.parentCommentId ?? undefined) : undefined
              void handleDeleteComment(postId, comment.id, parentId)
            }}
          />
        )}
        {depth === 0 && replyingToId === comment.id && (
          <Box sx={{ width: '100%', marginTop: 8 }}>
            <CommentInputRow
              input={{
                value: replyText,
                onChange: (e) => setReplyText(e.currentTarget.value),
                placeholder: `Reply to ${comment.authorUsername ?? 'user'}…`,
                disabled: commentSubmitting,
                multiline: true,
                minRows: 1,
                maxRows: 3
              }}
              action={(
                <AppButton
                  variant="light"
                  size="xs"
                  leftIcon={<FiSend size={12} />}
                  loading={commentSubmitting}
                  disabled={!replyText.trim()}
                  onClick={() => void handleAddReply(postId, comment.id)}
                >
                  Reply
                </AppButton>
              )}
            />
          </Box>
        )}
      </Group>
    )
  }

  const canManage =
    selectedPost &&
    (selectedPost.userId === currentUser?.id || isAdmin)

  const renderDesktopActions = () => {
    if (!selectedPost) return null
    return (
      <Group position="apart" grow sx={{ display: { base: 'none', sm: 'flex' } }}>
        <AppButton
          variant="light"
          leftIcon={<FiHeart size={16} fill={selectedPost.isLikedByCurrentUser ? 'currentColor' : 'none'} />}
          onClick={() => requireAuth(() => handleToggleLike(selectedPost.id))}
        >
          {selectedPost.likeCount} Like{selectedPost.likeCount !== 1 ? 's' : ''}
        </AppButton>
        <AppButton variant="light" leftIcon={<FiShare2 size={16} />} onClick={() => void handleShare(selectedPost)}>
          {shareCopied ? 'Copied!' : 'Share'}
        </AppButton>
        {canManage && (
          <AppButton
            variant="light"
            leftIcon={<FiEdit2 size={16} />}
            onClick={() => {
              history.push(`/slop-feed/${selectedPost.id}`)
              setSelectedPost(null)
            }}
          >
            Edit
          </AppButton>
        )}
        {canManage && (
          <AppButton
            color="red"
            variant="light"
            leftIcon={<FiTrash2 size={16} />}
            onClick={() => setDeleteConfirmId(selectedPost.id)}
          >
            Delete
          </AppButton>
        )}
      </Group>
    )
  }

  const renderMobileActions = () => {
    if (!selectedPost) return null
    return (
      <Group
        spacing="md"
        align="center"
        wrap="nowrap"
        sx={{ ...FEED_ACTION_BAR_SX, display: { base: 'flex', sm: 'none' } }}
      >
        <AppIconButton
          variant="subtle"
          size="lg"
          sx={slopThumbActionIconSx}
          onClick={() => requireAuth(() => handleToggleLike(selectedPost.id))}
          aria-label={selectedPost.isLikedByCurrentUser ? 'Unlike' : 'Like'}
        >
          <FiHeart
            size={22}
            fill={selectedPost.isLikedByCurrentUser ? 'currentColor' : 'none'}
          />
        </AppIconButton>
        <AppIconButton
          variant="subtle"
          size="lg"
          sx={slopThumbActionIconSx}
          onClick={scrollToComments}
          aria-label="Comments"
        >
          <FiMessageSquare size={22} />
        </AppIconButton>
        <AppIconButton
          variant="subtle"
          size="lg"
          sx={{ marginLeft: 'auto', ...slopThumbActionIconSx }}
          onClick={() => void handleShare(selectedPost)}
          aria-label={shareCopied ? 'Link copied' : 'Share'}
        >
          <FiShare2 size={22} />
        </AppIconButton>
        {canManage && (
          <AppIconButton
            variant="subtle"
            size="lg"
            sx={slopThumbActionIconSx}
            onClick={() => {
              history.push(`/slop-feed/${selectedPost.id}`)
              setSelectedPost(null)
            }}
            aria-label="Edit"
          >
            <FiEdit2 size={22} />
          </AppIconButton>
        )}
        {canManage && (
          <AppIconButton
            variant="subtle"
            size="lg"
            sx={slopThumbActionIconSx}
            onClick={() => setDeleteConfirmId(selectedPost.id)}
            aria-label="Delete"
          >
            <FiTrash2 size={22} />
          </AppIconButton>
        )}
      </Group>
    )
  }

  return (
    <AppModal
      opened={selectedPost !== null}
      onClose={() => setSelectedPost(null)}
      size="lg"
      title={selectedPost?.title ?? ''}
      styles={slopPostDetailModalStyles}
    >
      {selectedPost && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            [SLOP_FEED_MOBILE_MQ]: {
              maxHeight: 'calc(100dvh - 52px)',
              minHeight: 0
            }
          }}
        >
          {selectedPost.isEmbedded
            ? (
            <Box sx={slopDetailEmbedSx}>
              <iframe
                title={selectedPost.title ?? 'Embedded video'}
                src={selectedPost.embedSourceUrl ?? ''}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allowFullScreen
              />
            </Box>
              )
            : (
            <Box
              component="video"
              controls
              playsInline
              sx={{ ...slopDetailNativeVideoSx, ...slopDetailVideoBleedSx }}
            >
              <source src={toAbsoluteMediaUrl(selectedPost.videoUrl)} />
            </Box>
              )}

          <Box
            sx={{
              flex: { base: 1, sm: 'unset' },
              overflowY: { base: 'auto', sm: 'visible' },
              minHeight: 0,
              padding: { base: '12px', sm: 0 }
            }}
          >
            <Stack gap="md">
              <AppText sx={{ fontSize: 14, color: 'var(--gs-muted)' }}>{selectedPost.description}</AppText>

              <Group position="apart">
                <Stack gap={0}>
                  <AppText sx={{ fontSize: 12, fontWeight: 600 }}>{selectedPost.username}</AppText>
                  <AppText sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                    {formatSlopDate(selectedPost.createdAt)}
                  </AppText>
                </Stack>
                <AppButton
                  variant="subtle"
                  size="xs"
                  compact
                  onClick={() => {
                    const id = selectedPost.id
                    setSelectedPost(null)
                    history.push(`/slop-feed/${id}`)
                  }}
                >
                  Open full page →
                </AppButton>
              </Group>

              {(selectedPost.firearmModelId || selectedPost.cartridgeSpecId) && (
                <Stack gap={6}>
                  {selectedPost.firearmModelId && (
                    <Paper
                      withBorder
                      p={8}
                      sx={{ cursor: 'pointer', '&:hover': { borderColor: 'var(--gs-accent)' } }}
                      onClick={() => {
                        history.push(`/registry/${selectedPost.firearmModelId}`)
                        setSelectedPost(null)
                      }}
                    >
                      <AppText size="sm" fw={600}>{selectedPost.firearmModelName || 'Linked firearm'}</AppText>
                    </Paper>
                  )}
                  {selectedPost.cartridgeSpecId && (
                    <Paper
                      withBorder
                      p={8}
                      sx={{ cursor: 'pointer', '&:hover': { borderColor: 'var(--gs-accent)' } }}
                      onClick={() => {
                        history.push(`/ammo/specs/${selectedPost.cartridgeSpecId}`)
                        setSelectedPost(null)
                      }}
                    >
                      <AppText size="sm" fw={600}>{selectedPost.cartridgeLabel || 'Linked cartridge'}</AppText>
                    </Paper>
                  )}
                </Stack>
              )}

              {renderDesktopActions()}

              <Box id="slop-modal-comments" sx={{ borderTop: '1px solid var(--gs-border)', paddingTop: 16 }}>
                <AppText sx={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
                  {selectedPost.commentCount} Comment{selectedPost.commentCount !== 1 ? 's' : ''}
                </AppText>

                {isLoggedIn
                  ? (
                  <Box sx={{ marginBottom: 16 }}>
                    <CommentInputRow
                      input={{
                        value: newComment,
                        onChange: (e) => setNewComment(e.currentTarget.value),
                        placeholder: 'Add a comment…',
                        disabled: commentSubmitting,
                        multiline: true,
                        minRows: 1,
                        maxRows: 4
                      }}
                      action={(
                        <AppButton
                          variant="light"
                          size="sm"
                          leftIcon={<FiSend size={14} />}
                          loading={commentSubmitting}
                          disabled={!newComment.trim()}
                          onClick={() => void handleAddComment(selectedPost.id)}
                        >
                          Post
                        </AppButton>
                      )}
                    />
                  </Box>
                    )
                  : (
                  <Alert icon={<FiMessageSquare size={14} />} sx={{ marginBottom: 12, fontSize: 13 }}>
                    <AppText component="span">
                      <AppText
                        component="a"
                        onClick={() => history.push('/register')}
                        sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Sign up
                      </AppText>{' '}
                      or log in to comment.
                    </AppText>
                  </Alert>
                    )}

                <OneLevelCommentList
                  comments={comments}
                  loading={commentsLoading}
                  formatCreatedAt={formatSlopDate}
                  renderActions={renderCommentActions}
                />
              </Box>
            </Stack>
          </Box>

          {renderMobileActions()}
        </Box>
      )}
    </AppModal>
  )
}
